import React from "react";

import {
  accessibilityPageSchema,
  breadcrumbSchema,
} from "../seo/structuredData";
import { POLICY_LAST_UPDATED, PRODUCT } from "../seo/siteMeta";

import ContentPage from "./ContentPage";

/**
 * Accessibility statement.
 *
 * Written to the same rule as the security page: every claim is one the code actually supports, and
 * the gaps are stated rather than left for somebody to discover. A statement that says "fully
 * accessible" is worth nothing to the person who then cannot use the site, and it removes the one
 * thing that is genuinely useful here, which is knowing where the rough edges are and who to tell.
 */
const sections = [
  {
    heading: "What we aim for",
    body: [
      "This site targets WCAG 2.2 Level AA. That is the level most public bodies are held to, and it is the level we hold this site to whether or not anyone requires it of us. A merit badge college is a volunteer event that anyone in a unit might be asked to organize, and the software around it should not be the thing that decides who can.",
      "Accessibility is checked automatically on every build rather than audited once and forgotten. The build runs an axe accessibility scan against every page after rendering it, and fails if a page introduces a violation, alongside the same check running in the test suite and in the linter.",
    ],
  },
  {
    heading: "What this site does",
    bullets: [
      "Every page can be operated with a keyboard alone, and a skip link at the top of each page jumps straight past the navigation to the main content.",
      "Text and interface colours meet the AA contrast minimum in both the light and dark colour schemes, and the site follows whichever your device asks for.",
      "The page structure is described properly: one main heading per page, headed sections beneath it, and landmarks for the navigation, the main content, and the footer.",
      "Form fields have visible labels, required fields are marked as required, and when something is wrong the reason is attached to the field itself rather than shown only as colour.",
      "The animated badge backdrop on the home page does not play for anyone whose device asks for reduced motion.",
      "Decorative images and icons are hidden from screen readers instead of being read out as filenames.",
      "Text reflows and stays legible when zoomed or viewed on a narrow screen, without a second scrollbar appearing across the page.",
    ],
  },
  {
    heading: "Known limitations",
    body: [
      "These are the parts we know are not there yet. We would rather tell you than have you find out:",
    ],
    bullets: [
      "The live demo on the demo page is the product itself, embedded in a frame. It is a separate application with its own accessibility work in progress, and it is not covered by the checks that run against this site.",
      "Payment is completed on PayPal's own checkout, which is outside our control. If you hit a barrier there, tell us anyway: we need to know, and we can take a registration another way.",
      "The site has not been through a formal third-party accessibility audit, and it has not been tested with every combination of screen reader and browser. Automated checks catch a good share of problems but not the ones that only a person notices.",
      "Some of the merit badge artwork is decorative and carries no text alternative by design. Nothing is conveyed by those images alone.",
    ],
  },
  {
    heading: "Telling us about a problem",
    body: [
      `If any part of this site or of BadgeReg itself is difficult or impossible for you to use, email ${PRODUCT.supportEmail} and describe what happened, which page you were on, and what you were using (browser, and any assistive technology). You do not need to know the technical cause, and you do not need to phrase it in accessibility terms.`,
      "The person who writes the software is the person who reads that inbox, so a report goes straight to somebody who can fix it. We aim to reply within five working days, and to tell you either when it is fixed or what the plan is if it will take longer.",
      "If something is blocking you from registering a Scout right now, say so and we will get the registration done for you while the underlying problem is sorted out.",
    ],
  },
  {
    heading: "How this page is kept honest",
    body: [
      "The commitments above are enforced by the build rather than by intention. The colour tokens, the heading structure, and the labelling of controls are all covered by automated checks that run before the site can ship, so a change that breaks one of them fails rather than quietly reaching you.",
      "That is not the same as being perfect, and this page will be updated as the gaps listed above are closed.",
    ],
  },
];

export default function Accessibility() {
  return (
    <ContentPage
      route="/accessibility"
      heading="Accessibility"
      badge="DisabilitiesAwareness.svg"
      badgeLabel="Disabilities Awareness"
      standfirst="What BadgeReg's marketing site does to be usable by everyone, the places it currently falls short, and how to tell us when it fails you."
      effectiveDate={POLICY_LAST_UPDATED}
      sections={sections}
      jsonLd={[
        accessibilityPageSchema,
        breadcrumbSchema("Accessibility", "/accessibility"),
      ]}
    />
  );
}
